// Kid Today — landing surface inside KidApp.
//
// Layout (mobile-first, big-tap-friendly):
//   1. Hero: big avatar + name + "⭐ N this week" + greeting copy.
//   2. Tonight's dinner: a single chunky tile pulled from the family
//      meal plan (household-scoped via mealplan.listForRange).
//   3. Up next: today's calendar events from the household (events
//      shared family-wide or tagged for this kid). Auto-hidden if
//      nothing's on today.
//   4. Today's chores: up to 3 not-yet-done tiles + a "see all" link.
//
// Data: pulls choresListForKid, choresStarsThisWeek, mealplanListForRange,
// and calendarListEvents. The calendar query uses today's start/end in
// the kid's local TZ so the "today" boundary matches their wall clock,
// not the server's UTC.

import { useEffect, useMemo, useState } from "preact/hooks";
import { api, type KidChore, type CalendarEvent, type MealPlanEntry } from "../../lib/rpc";
import type { Me } from "../../lib/use-me";
import { KidBankCard } from "./bank-card";

interface KidTodayProps {
  me: Me;
  notify: (msg: string) => void;
  onGoChores: () => void;
}

/** YYYY-MM-DD in the device's local timezone — kids in the Hub PWA all
 *  share the same TZ as the device they're holding, so we trust the
 *  browser here rather than calling out for MIRROR_ALLDAY_TZ. */
function localDateKey(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "3:30pm" — short event-time pill. All-day events return null and the
 *  caller renders an "all day" label instead. */
function formatEventTime(iso: string, allDay: boolean): string | null {
  if (allDay) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const h = d.getHours();
  const m = d.getMinutes();
  const hh = ((h + 11) % 12) + 1;
  const mm = String(m).padStart(2, "0");
  return `${hh}:${mm}${h < 12 ? "am" : "pm"}`;
}

export function KidTodayView({ me, notify, onGoChores }: KidTodayProps) {
  const [chores, setChores] = useState<KidChore[]>([]);
  const [stars, setStars] = useState(0);
  const [dinner, setDinner] = useState<string | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const todayKey = useMemo(() => localDateKey(), []);
  const now = useMemo(() => new Date(), []);

  const refresh = async () => {
    try {
      // Fan out the four independent reads in parallel so the screen
      // paints in roughly one round-trip. Each failure is swallowed
      // back to a sensible default — the Today screen always renders,
      // even if (say) the calendar service is down.
      const [c, s, mp, ev] = await Promise.all([
        api.choresListForKid(me.username).catch(() => [] as KidChore[]),
        api.choresStarsThisWeek(me.username).catch(() => 0),
        api.mealplanListForRange(todayKey, todayKey).catch(() => [] as MealPlanEntry[]),
        // Range is the whole local day. The server returns events that
        // intersect this window; we then filter to "for me or for the
        // whole household" client-side.
        api.calendarListEvents({
          rangeStart: `${todayKey}T00:00:00`,
          rangeEnd: `${todayKey}T23:59:59`,
        }).catch(() => [] as CalendarEvent[]),
      ]);
      setChores(c);
      setStars(s);
      // Dinner slot specifically — breakfast and lunch are skipped on
      // the kid screen (the family-meal-plan story is "what's for
      // dinner" not a three-meal ledger).
      setDinner(mp.find((e) => e.slot === "dinner")?.value || null);
      // Events filter: household-wide events (forUserId === null) OR
      // tagged for THIS kid. Other family members' personal events
      // stay hidden. Sort by start.
      const mine = ev.filter((e) => !e.forUserId || e.forUserId === me.username);
      mine.sort((a, b) => a.dtstart.localeCompare(b.dtstart));
      setEvents(mine);
    } catch (err) {
      notify((err as Error)?.message || "couldn't load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, [me.username, todayKey]);

  const onToggle = async (c: KidChore) => {
    try {
      const r = await api.choresToggleDone(c.id);
      if (r.action === "done") {
        notify(r.pendingApproval ? "Waiting for parent OK!" : `⭐ +${r.starsAwarded || 0}`);
      } else {
        notify("Undone");
      }
      await refresh();
    } catch (err) {
      notify((err as Error)?.message || "couldn't update");
    }
  };

  const todoChores = chores.filter((c) => !c.doneToday);
  const visibleTodo = todoChores.slice(0, 3);
  const doneCount = chores.length - todoChores.length;

  return (
    <div className="kid-today">
      <section className="kid-hero">
        <div
          className="kid-hero-avatar"
          style={{ background: me.avatarColor || "var(--accent)" }}
        >
          {me.avatarEmoji || me.initial}
        </div>
        <h1 className="kid-hero-name">Hi, {me.displayName}!</h1>
        <div className="kid-hero-stars">⭐ {stars} this week</div>
      </section>

      <KidBankCard me={me} notify={notify} refreshToken={chores.length + doneCount} />

      {loading && <div className="kid-empty">loading…</div>}

      {/* Tonight's dinner — small banner card. Render only when the
          family has actually planned something; an empty slot here would
          be a sad "nothing" tile, which we'd rather omit. */}
      {!loading && dinner && (
        <section className="kid-today-dinner">
          <div className="kid-dinner-icon">🍽️</div>
          <div className="kid-dinner-body">
            <div className="kid-dinner-label">Tonight's dinner</div>
            <div className="kid-dinner-name">{dinner}</div>
          </div>
        </section>
      )}

      {/* Up next today — the kid's calendar at-a-glance. Empty state
          omitted on purpose: a quiet day shouldn't have a "no events"
          card cluttering things up. */}
      {!loading && events.length > 0 && (
        <section className="kid-today-events">
          <header className="kid-section-head">
            <h2 className="kid-section-title">Up next</h2>
            <div className="kid-section-meta">
              {events.length} {events.length === 1 ? "event" : "events"}
            </div>
          </header>
          <div className="kid-event-list">
            {events.slice(0, 4).map((e) => {
              const time = formatEventTime(e.dtstart, e.allDay);
              return (
                <div key={e.id} className="kid-event-row">
                  <div className="kid-event-time">
                    {time || "all day"}
                  </div>
                  <div className="kid-event-title">{e.title || "Untitled"}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {!loading && chores.length === 0 && (
        <div className="kid-empty kid-empty-hero">
          <div className="kid-empty-emoji">🎈</div>
          <div className="kid-empty-title">No chores today!</div>
          <div className="kid-empty-sub">Have fun.</div>
        </div>
      )}

      {!loading && chores.length > 0 && (
        <section className="kid-today-chores">
          <header className="kid-section-head">
            <h2 className="kid-section-title">Today</h2>
            <div className="kid-section-meta">
              {doneCount}/{chores.length} done
            </div>
          </header>

          {todoChores.length === 0 ? (
            <div className="kid-empty kid-empty-card">
              <div className="kid-empty-emoji">🎉</div>
              <div className="kid-empty-title">All done!</div>
              <div className="kid-empty-sub">Come back tomorrow.</div>
            </div>
          ) : (
            <div className="kid-tile-grid">
              {visibleTodo.map((c) => (
                <KidChoreTile key={c.id} chore={c} onToggle={() => onToggle(c)} />
              ))}
            </div>
          )}

          {todoChores.length > 3 && (
            <button type="button" className="kid-see-all" onClick={onGoChores}>
              See all {todoChores.length} →
            </button>
          )}
        </section>
      )}
    </div>
  );
}

interface KidChoreTileProps {
  chore: KidChore;
  onToggle: () => void;
}

function KidChoreTile({ chore, onToggle }: KidChoreTileProps) {
  const denied = !!chore.deniedAt;
  // Today tile mirrors KidChoresView's lock — once stars are out,
  // can't tap to re-earn. Anti-farm guard. Server enforces too.
  const pendingApproval =
    chore.doneToday && chore.requiresApproval && chore.approvedAt == null;
  const locked = chore.doneToday && !pendingApproval && !denied;
  return (
    <button
      type="button"
      className={
        "kid-tile"
        + (chore.doneToday ? " is-done" : "")
        + (denied ? " is-denied" : "")
        + (locked ? " is-locked" : "")
      }
      onClick={locked ? undefined : onToggle}
      disabled={locked}
      aria-disabled={locked}
    >
      <span className="kid-tile-icon">{denied ? "🙈" : (chore.icon || "✅")}</span>
      <span className="kid-tile-name">{chore.name}</span>
      {denied && (
        <span className="kid-tile-denial">
          {chore.denialReason ? `Needs another look · ${chore.denialReason}` : "Needs another look"}
        </span>
      )}
      <span className="kid-tile-stars">⭐ {chore.stars}</span>
    </button>
  );
}
